import { UserData } from "@opendash/plugin-parse/dist/types-generated";
import Parse, { User } from "parse";
import { Agency, Route, Stop, Stop_Times, Trip } from "../../parsegenerated";
import { JSONData } from "../KommuterConverter";
import { toAgencyModel } from "../models/agency";
import { toRouteModel } from "../models/route";
import { toStopModel } from "../models/stop";
import { toStopTimeModel } from "../models/stopTime";
import { toTripModel } from "../models/trip";
import { RouteConverter } from "./RouteConterver";

/**
 * Converts Kommuter offers to GTFS data
 */
export class GTFSConverter {
  private allOffers: any[] = [];
  private allPlaces: any[][] = []; // Array of places for each offer matching the index

  // converter
  private routeConverter: RouteConverter[] = [];

  // GTFS data models
  private agency: Agency;
  private routes: Route[] = [];
  private trips: Trip[] = [];
  private stops: Stop[] = [];
  private stopTimes: Stop_Times[] = [];

  constructor() {
    this.agency = new Agency({
      agency_id: "kommuter",
      agency_name: "Kommuter - " + Parse.User.current()?.get("username"),
      agency_url: "https://kommuter.app",
      agency_timezone: "Europe/Berlin",
      agency_lang: "de",
      agency_email: Parse.User.current()?.getEmail(),
      agency_fare_url: "",
    });
  }

  public async convertToGTFS() {
    await this.fetchOffers();
    await this.fetchPlacesInOffers();
    this.convertRoutes();
    this.fillGTFSData();

    return this;
  }

  public toJSON() {
    return JSON.stringify({
      agency: toAgencyModel(this.agency),
      routes: this.routes.map((route) => toRouteModel(route)),
      trips: this.trips.map((trip) => toTripModel(trip)),
      stops: this.stops.map((stop) => toStopModel(stop)),
      stopTimes: this.stopTimes.map((st) => toStopTimeModel(st)),
    });
  }

  public getJSONData() {
    return {
      agency: toAgencyModel(this.agency),
      routes: this.routes.map((route) => toRouteModel(route)),
      trips: this.trips.map((trip) => toTripModel(trip)),
      stops: this.stops.map((stop) => toStopModel(stop)),
      stopTimes: this.stopTimes.map((st) => toStopTimeModel(st)),
    } as JSONData;
  }

  public getData() {
    return {
      agency: this.agency,
      routes: this.routes,
      trips: this.trips,
      stops: this.stops,
      stopTimes: this.stopTimes,
    };
  }

  private async fetchOffers() {
    const notAllowedFieldQuery = new Parse.Query(UserData)
      .include("user")
      .equalTo("key", "allowExport")
      .equalTo("value", "0");

    const notAllowedUsers = (await notAllowedFieldQuery.find()).map(
      (ud) => ud.get("user")?.id
    );

    const allowedUserQuery = new Parse.Query(User).notContainedIn(
      "objectId",
      notAllowedUsers
    );

    this.allOffers = await new Parse.Query("RideSharingOffer")
      .matchesQuery("user", allowedUserQuery)
      .include("route")
      .find();
  }

  private async fetchPlacesInOffers() {
    const placesQueries = this.allOffers.map((offer) => offer.get("places"));
    this.allPlaces = (await Promise.all(
      placesQueries.map((places) => places?.query()?.find())
    )) as any[][];
  }

  private convertRoutes() {
    this.routeConverter = this.allOffers.map((offer, index) => {
      return new RouteConverter(
        this.agency,
        offer,
        this.allPlaces[index],
        index
      );
    });
  }

  private fillGTFSData() {
    this.routeConverter.forEach((rC) => {
      const route = rC.getRoute();
      if (route) this.routes.push(route);
      const trip = rC.getTrip();
      if (trip) {
        trip.set("service_id", rC.getCalendar());
        this.trips.push(trip);
      }
      const tripStopTimes = rC.getStopTimes();
      if (tripStopTimes?.length) this.stopTimes.push(...tripStopTimes);
      const stops = rC.getStops();
      if (stops?.length) this.stops.push(...stops);
    });
  }
}
